import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  User,
  UserCredential,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';
import { UserDocument, SubscriptionTier } from '@curbside-quest/shared';

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Auth service functions
export const authService = {
  // Sign up with email and password
  async signUpWithEmail(email: string, password: string, displayName: string): Promise<UserCredential> {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update the user's display name
    await updateProfile(userCredential.user, { displayName });
    
    // Create user profile document
    await createUserProfile(userCredential.user, 'magic-link');
    
    return userCredential;
  },

  // Sign in with email and password
  async signInWithEmail(email: string, password: string): Promise<UserCredential> {
    return await signInWithEmailAndPassword(auth, email, password);
  },

  // Sign in with Google
  async signInWithGoogle(): Promise<UserCredential> {
    const userCredential = await signInWithPopup(auth, googleProvider);
    
    // Check if this is a new user and create profile if needed
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
    if (!userDoc.exists()) {
      await createUserProfile(userCredential.user, 'google');
    }
    
    return userCredential;
  },

  // Sign out
  async signOut(): Promise<void> {
    return await signOut(auth);
  },

  // Send password reset email
  async sendPasswordResetEmail(email: string): Promise<void> {
    return await sendPasswordResetEmail(auth, email);
  },

  // Get current user
  getCurrentUser(): User | null {
    return auth.currentUser;
  },
};

// Create user profile document in Firestore
async function createUserProfile(user: User, authProvider: 'google' | 'magic-link'): Promise<void> {
  const userDoc: Omit<UserDocument, 'createdAt' | 'updatedAt'> & {
    createdAt: any;
    updatedAt: any;
  } = {
    id: user.uid,
    email: user.email || '',
    displayName: user.displayName || '',
    authProvider,
    subscriptionTier: 'free' as SubscriptionTier,
    goldCoins: 0,
    farAwayTokens: 3, // Free users get 3 tokens per month
    locationRadius: 2000, // 2km radius for free users
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(doc(db, 'users', user.uid), userDoc);
}

// Get user profile from Firestore
export async function getUserProfile(userId: string): Promise<UserDocument | null> {
  const userDoc = await getDoc(doc(db, 'users', userId));
  return userDoc.exists() ? (userDoc.data() as UserDocument) : null;
}

// Update user profile in Firestore
export async function updateUserProfile(userId: string, updates: Partial<UserDocument>): Promise<void> {
  const userRef = doc(db, 'users', userId);
  await setDoc(userRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}